import React, { useState, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Lock, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';

const ChangePassword = () => {
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'New passwords do not match',
        life: 3000
      });
      return;
    }

    setLoading(true);
    try {
      // API call would go here
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulated API call
      
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Password changed successfully',
        life: 3000
      });
      
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to change password',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 py-12">
      <Toast ref={toast} />
      
      <div className="mx-auto max-w-lg px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#3730a3] to-[#3730a3]/80 px-8 py-6">
            <div className="flex items-center">
              <div className="bg-white/20 p-3 rounded-lg mr-4">
                <Lock className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Change Password</h1>
                <p className="text-white/80 text-sm mt-1">Update your password to keep your account secure</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-8">
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700 flex items-center">
                Current Password
              </label>
              <div className="relative">
                <InputText
                  type={showPassword.current ? "text" : "password"}
                  className="w-full bg-gray-50 border-gray-200 focus:border-[#3730a3] focus:ring-[#3730a3]/20 pl-10 pr-10 py-2.5 rounded-lg"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  name="currentPassword"
                  required
                />
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <button 
                  type="button" 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  onClick={() => togglePasswordVisibility('current')}
                >
                  {showPassword.current ? 
                    <EyeOff className="h-5 w-5 text-gray-400" /> : 
                    <Eye className="h-5 w-5 text-gray-400" />
                  }
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700 flex items-center">
                New Password
              </label>
              <div className="relative">
                <InputText
                  type={showPassword.new ? "text" : "password"}
                  className="w-full bg-gray-50 border-gray-200 focus:border-[#3730a3] focus:ring-[#3730a3]/20 pl-10 pr-10 py-2.5 rounded-lg"
                  value={formData.newPassword}
                  onChange={handleChange}
                  name="newPassword"
                  required
                />
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <button 
                  type="button" 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  onClick={() => togglePasswordVisibility('new')}
                >
                  {showPassword.new ? 
                    <EyeOff className="h-5 w-5 text-gray-400" /> : 
                    <Eye className="h-5 w-5 text-gray-400" />
                  }
                </button>
              </div>
              
              <div className="flex items-center text-xs text-gray-500 mt-2">
                <AlertCircle className="h-4 w-4 mr-1 text-[#3730a3]" />
                Password should be at least 8 characters long with numbers and special characters
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700 flex items-center">
                Confirm New Password
              </label>
              <div className="relative">
                <InputText
                  type={showPassword.confirm ? "text" : "password"}
                  className="w-full bg-gray-50 border-gray-200 focus:border-[#3730a3] focus:ring-[#3730a3]/20 pl-10 pr-10 py-2.5 rounded-lg"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  name="confirmPassword"
                  required
                />
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <button 
                  type="button" 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  onClick={() => togglePasswordVisibility('confirm')}
                >
                  {showPassword.confirm ? 
                    <EyeOff className="h-5 w-5 text-gray-400" /> : 
                    <Eye className="h-5 w-5 text-gray-400" />
                  }
                </button>
              </div>
            </div>

            <div className="pt-6">
              <Button
                type="submit"
                label={loading ? "Changing Password..." : "Change Password"}
                icon="pi pi-lock"
                iconPos="left"
                className="w-full bg-[#3730a3] hover:bg-[#3730a3]/90 py-4 text-base font-medium rounded-lg shadow-md"
                loading={loading}
                style={{ height: "auto" }}
                pt={{
                  icon: { className: 'ml-3' }
                }}
              />
            </div>
          </form>
          
          <div className="px-8 py-6 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Check className="h-5 w-5 text-green-500" />
              <span>Changes will apply to your account immediately</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword; 